import { shorten } from '@polaris/core/utils'

export interface ParsedMpcError {
  type: 'INSUFFICIENT_GAS' | 'GENERIC_ERROR'
  message: string
  fullMessage?: string
  details?: {
    ticker?: string
    amountNeeded?: string
    chainName?: string
    address?: string
  }
}

/**
 * Parses MPC error messages to extract user-friendly information
 * Specifically handles the gas requirement error from mpc.go line 1031
 */
export function parseMpcError(error: unknown): ParsedMpcError {
  let errorMessage = ''

  // Handle different error formats
  if (error instanceof Error) {
    // Check if the error has a cause (ky wraps API errors this way)
    if (error.cause instanceof Error) {
      errorMessage = error.cause.message
    } else if (typeof error.cause === 'string') {
      errorMessage = error.cause
    } else {
      errorMessage = error.message
    }
  } else if (typeof error === 'object' && error !== null && 'error' in error) {
    // Handle API response format { "error": "..." }
    errorMessage = String((error as { error: unknown }).error)
  } else {
    errorMessage = String(error)
  }

  // Pattern: "gas requirement is greater than the amount to refund for address ... on chain ...: ... is greater than ... . Fund ... ... more"
  const gasRequirementRegex =
    /gas requirement is greater than the amount to refund for address ([^\s]+) on chain (.*?):\s*[0-9.]+ is greater than [0-9.]+ \. Fund ([0-9.]+) ([A-Za-z]+) more/
  const match = errorMessage.match(gasRequirementRegex)

  if (match) {
    const [, address, chainName, amountNeeded, ticker] = match
    const trimmedChainName = chainName.trim()
    return {
      type: 'INSUFFICIENT_GAS',
      message: `You need ${amountNeeded} ${ticker} more for gas fees on ${trimmedChainName} for address ${shorten(address)}`,
      fullMessage: `You need ${amountNeeded} ${ticker} more for gas fees on ${trimmedChainName} for address ${address}`,
      details: {
        address,
        chainName: trimmedChainName,
        amountNeeded,
        ticker,
      },
    }
  }

  // Return generic error for unrecognized patterns
  return {
    type: 'GENERIC_ERROR',
    message: 'Failed to recover funds. Please try again.',
  }
}
